import { z } from "zod";

// Contrat frontend du dashboard détail projet (run-003, spec §2.3/§2.5).
// Validation Zod à la frontière fetch : TS strict est OFF, ce schéma EST le contrat runtime.
// Pas de .strict() : une clé ajoutée côté backend ne doit pas casser le front
// (comportement Zod par défaut : les clés inconnues sont retirées de l'objet parsé).
// Enums fermés uniquement là où un CHECK SQL les garantit (migrations 185/186/187) ;
// `status` d'une entry reste TEXT libre en SQL (validation applicative backend) → string.

export const pilotageDimensionKeySchema = z.enum(["metier", "dev", "client"]);
export type PilotageDimensionKey = z.infer<typeof pilotageDimensionKeySchema>;

export const pilotageEntryKindSchema = z.enum(["waiting", "rdv", "feature", "step", "milestone"]);
export type PilotageEntryKind = z.infer<typeof pilotageEntryKindSchema>;

export const pilotageClientStatusSchema = z.enum(["en_bonne_voie", "livre"]);
export type PilotageClientStatus = z.infer<typeof pilotageClientStatusSchema>;

// Signal 4 niveaux (M6, run-005 story 2) — miroir EXACT du CHECK migration 189
// (backend/migrations/189_pilotage_detail_v1.sql:28). Saisi Ailix, coexiste avec le binaire
// client_status (aucun ne dérive de l'autre).
export const pilotageSignalSchema = z.enum([
  "ras",
  "a_surveiller",
  "action_attendue",
  "decision_a_prevoir",
]);
export type PilotageSignal = z.infer<typeof pilotageSignalSchema>;

// Ball-in-court (M3) — miroir CHECK 189:17 (deviseur_pilotage_entries).
export const pilotageBallInCourtSchema = z.enum(["ailix", "client", "externe"]);
export type PilotageBallInCourt = z.infer<typeof pilotageBallInCourtSchema>;

// Enums des 4 objets story 2 — miroirs EXACTS des CHECK migration 189 (une divergence de slug
// serait un 500 backend ou un rejet Zod silencieux à la lecture).
export const pilotageJournalEventTypeSchema = z.enum([
  "echange",
  "decision",
  "livraison",
  "demande",
  "bascule",
  "run",
  "autre",
]);
export type PilotageJournalEventType = z.infer<typeof pilotageJournalEventTypeSchema>;

export const pilotageJournalVisibilitySchema = z.enum(["client", "interne"]);
export const pilotageJournalSubjectTypeSchema = z.enum(["feature", "rdv", "demande"]);

export const pilotageRequestStatusSchema = z.enum([
  "en_attente_dec",
  "validee",
  "refusee",
  "reportee",
]);
export type PilotageRequestStatus = z.infer<typeof pilotageRequestStatusSchema>;

export const pilotageFoundationStatusSchema = z.enum(["prevu", "en_cours", "en_place"]);
export type PilotageFoundationStatus = z.infer<typeof pilotageFoundationStatusSchema>;

export const pilotageReportTypeSchema = z.enum(["cr_hebdo", "rapport_run"]);
export type PilotageReportType = z.infer<typeof pilotageReportTypeSchema>;

const pilotageStageSchema = z.number().int().min(1).max(6);

// `duration_days` et `price` retirés le 2026-08-18 (R005-FUITE-CLIENT-001) : la route dashboard
// n'est pas authentifiée, et un jour vendu ou un prix présent dans la réponse HTTP est exposé,
// qu'il soit rendu ou non. Ne pas les remettre. Le schéma n'est pas `.strict()`, il tolère donc
// un backend qui les sert encore — c'est ce qui autorise le déploiement front-d'abord.
const pilotageDashboardMissionSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  team: z.string().nullable(),
  priorisation: z.string().nullable(),
  status: z.string().nullable(),
  // REFONDATION-PILOTAGE-LOT1.0 : sans session salarié, le serveur omet ces trois clés (absentes,
  // pas null). Optionnelles ici pour que la page projet parse la réponse client ; ce front part
  // avant ou avec le serveur, sinon une clé requise absente met l'écran en erreur.
  assigned_developer_id: z.string().nullable().optional(),
  developer_name: z.string().nullable().optional(),
  developer_color: z.string().nullable().optional(),
});

// ─── Contrat §6 (SPEC_DEV_NUIT_PILOTAGE, figé le 30-10) : clés neuves du dashboard ───
export const pilotageDisplayStateSchema = z.enum([
  "planifiee",
  "en_cours",
  "en_attente_client",
  "bloquee",
  "en_pause",
  "en_production",
  "terminee",
  "abandonnee",
]);
export type PilotageDisplayState = z.infer<typeof pilotageDisplayStateSchema>;

export const pilotageNextMilestoneSchema = z.object({
  label: z.string(),
  date: z.string(),
  kind: z.enum(["version", "target", "pause_review"]),
});
export type PilotageNextMilestone = z.infer<typeof pilotageNextMilestoneSchema>;

export const pilotageBlockingSchema = z.object({
  entry_id: z.string(),
  label: z.string(),
  detail: z.string().nullable().optional(),
  since: z.string().nullable().optional(),
  ball_in_court: pilotageBallInCourtSchema.nullable().optional(),
  due_date: z.string().nullable().optional(),
  contact_label: z.string().nullable().optional(),
});
export type PilotageBlocking = z.infer<typeof pilotageBlockingSchema>;

export const pilotageVersionStateSchema = z.enum(["recette", "developpement", "chiffree"]);
export type PilotageVersionState = z.infer<typeof pilotageVersionStateSchema>;

export const pilotageUpcomingVersionSchema = z.object({
  id: z.string(),
  label: z.string(),
  state: pilotageVersionStateSchema,
  content: z.string().nullable().optional(),
  target_date: z.string().nullable().optional(),
});
export type PilotageUpcomingVersion = z.infer<typeof pilotageUpcomingVersionSchema>;

export const pilotageVersionsSchema = z.object({
  production: z
    .object({
      label: z.string(),
      date: z.string().nullable().optional(),
      // Précision de `date` (reprise de prod_version_date_precision) : une date au mois s'affiche
      // « mai 2026 » (chef de projet, 30-10).
      date_precision: z.enum(["day", "month"]).nullable().optional(),
      content: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  upcoming: z.array(pilotageUpcomingVersionSchema).optional().default([]),
});
export type PilotageVersions = z.infer<typeof pilotageVersionsSchema>;

export const pilotageNextRdvSchema = z.object({
  entry_id: z.string(),
  label: z.string(),
  date: z.string(),
  time: z.string().nullable().optional(),
});
export type PilotageNextRdv = z.infer<typeof pilotageNextRdvSchema>;

// `summary` vaut null tant que le résumé n'est pas validé (§4.4, filtrage serveur).
export const pilotageRdvSchema = z.object({
  entry_id: z.string(),
  label: z.string(),
  date: z.string().nullable(),
  time: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
});
export type PilotageRdv = z.infer<typeof pilotageRdvSchema>;

// Miroir de `_build_pilotage_item_response` (backend/app/api/v1/clients.py) SANS
// `subitems`/`subitem_stats` : le endpoint dashboard les retire de l'item — les
// subitems vivent au top-level de la réponse (spec §2.3).
export const pilotageDashboardItemSchema = z.object({
  id: z.string(),
  project_id: z.string(),
  mission_id: z.string().nullable(),
  custom_title: z.string().nullable(),
  title_snapshot: z.string().nullable(),
  title_override: z.string().nullable(),
  title: z.string(),
  pilotage_owner_name: z.string().nullable(),
  pilotage_stage: pilotageStageSchema,
  pilotage_health: z.string(),
  pilotage_caption: z.string().nullable(),
  pilotage_target_date: z.string().nullable(),
  pilotage_client_status: pilotageClientStatusSchema.nullable(),
  pilotage_client_status_reason: z.string().nullable(),
  // Signal 4 niveaux M6 (run-005 story 2) — sérialisé À CÔTÉ de client_status (clients.py:2781-2782).
  // `.nullable()` sans `.optional()` : la clé arrive AVEC l'extension dashboard story 2 (toujours
  // présente, valeur NULL si non saisie), contrairement à `detail` (retrofit cache → .optional()).
  pilotage_signal: pilotageSignalSchema.nullable(),
  pilotage_signal_reason: z.string().nullable(),
  estimated_delivery: z.string().nullable(),
  estimated_delivery_source: z.enum(["manual", "auto"]).nullable(),
  detail_url: z.string().nullable(),
  section_key: z.string(),
  section_title: z.string().nullable(),
  item_kind: z.string(),
  nature: z.string().nullable(),
  program_delivered_count: z.number().nullable(),
  program_pending_count: z.number().nullable(),
  sort_order: z.number(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  mission: pilotageDashboardMissionSchema.nullable(),
  // REFONDATION-PILOTAGE-LOT2-STORY-2.2 — Pause à périmètre (§3.7, migration 247).
  // 10 colonnes exposées par `_build_pilotage_item_response` + liste
  // `pause_feature_entry_ids` (ajoutée dashboard-only hors builder, migration 247 pause_features).
  // `.optional()` + `.default(...)` : tolérance anciennes réponses/cache avant déploiement.
  paused_since: z.string().nullable().optional().default(null),
  pause_reason: z.string().nullable().optional().default(null),
  pause_decided_by: z.string().nullable().optional().default(null),
  pause_review_date: z.string().nullable().optional().default(null),
  pause_reviewer_name: z.string().nullable().optional().default(null),
  pause_scope: z.enum(["project", "volet"]).nullable().optional().default(null),
  pause_scope_note: z.string().nullable().optional().default(null),
  pause_no_delivery: z.boolean().nullable().optional().default(null),
  pause_subitem_id: z.string().nullable().optional().default(null),
  paused_stage: pilotageStageSchema.nullable().optional().default(null),
  pause_feature_entry_ids: z.array(z.string()).optional().default([]),
  // Nouvelle page de pilotage, contrat §6 de SPEC_DEV_NUIT_PILOTAGE (30-10). Toutes facultatives :
  // la fiche s'affiche avec l'API ancienne. `.catch(undefined)` : une valeur inattendue sur une clé
  // neuve est ramenée à « absente » au lieu de mettre toute la fiche en erreur. Le bloc est alors
  // masqué, sauf pour `rdv_done_count`, `next_rdv` et `rdvs` (racine) : le front repasse sans
  // signal sur son calcul de repli depuis `entries`.
  display_state: pilotageDisplayStateSchema.optional().catch(undefined),
  group_id: z.string().nullable().optional().catch(undefined),
  // Nom de l'ensemble (Automail, Gates) pour le fil d'Ariane, ajouté au §6 le 30-10 (6ab645a7).
  group_title: z.string().nullable().optional().catch(undefined),
  next_milestone: pilotageNextMilestoneSchema.nullable().optional().catch(undefined),
  open_actions: z.object({ client: z.number(), ailix: z.number() }).optional().catch(undefined),
  blocking: z.array(pilotageBlockingSchema).optional().catch(undefined),
  versions: pilotageVersionsSchema.optional().catch(undefined),
  rdv_done_count: z.number().int().optional().catch(undefined),
  next_rdv: pilotageNextRdvSchema.nullable().optional().catch(undefined),
  closure_state: z.enum(["terminee", "abandonnee"]).nullable().optional().catch(undefined),
  closure_date: z.string().nullable().optional().catch(undefined),
  prod_version_content: z.string().nullable().optional().catch(undefined),
});
export type PilotageDashboardItem = z.infer<typeof pilotageDashboardItemSchema>;

export const pilotageDashboardSubitemSchema = z.object({
  id: z.string(),
  title: z.string(),
  pilotage_stage: pilotageStageSchema,
});
export type PilotageDashboardSubitem = z.infer<typeof pilotageDashboardSubitemSchema>;

export const pilotageDimensionSchema = z.object({
  id: z.string(),
  entity_type: z.enum(["item", "subitem"]),
  entity_id: z.string(),
  dimension: pilotageDimensionKeySchema,
  pct: z.number().int().min(0).max(100),
  caption: z.string().nullable(),
  sort_order: z.number(),
});
export type PilotageDimension = z.infer<typeof pilotageDimensionSchema>;

// Source d'un champ (REFONDATION-PILOTAGE-LOT2-STORY-2.1, migration 246).
// Projection publique : {source_kind, source_date} UNIQUEMENT — `source_ref` (interne
// salarié) et `note` NE DOIVENT JAMAIS être exposés par cette surface.
export const pilotageSourceKindSchema = z.enum(["mail", "transcription", "jira", "thomas", "manual"]);
export type PilotageSourceKind = z.infer<typeof pilotageSourceKindSchema>;

export const pilotageSourceSchema = z.object({
  source_kind: pilotageSourceKindSchema,
  source_date: z.string(),
});
export type PilotageSource = z.infer<typeof pilotageSourceSchema>;

export const pilotageEntrySchema = z.object({
  id: z.string(),
  entity_type: z.enum(["item", "subitem"]),
  entity_id: z.string(),
  kind: pilotageEntryKindSchema,
  dimension: pilotageDimensionKeySchema.nullable(),
  label: z.string(),
  // Détail long optionnel (R003-DETAIL-SPLIT.1, tous kinds, multiligne). `.nullable()` accepte
  // `null` (valeur renvoyée par le backend), `.optional()` accepte la clé absente (anciennes
  // réponses/caches) — les DEUX sont requis pour couvrir string | null | clé absente.
  detail: z.string().nullable().optional(),
  status: z.string().nullable(),
  entry_date: z.string().nullable(),
  sort_order: z.number(),
  // Ball-in-court (M3, run-005 story 2) — clés TOUJOURS sérialisées par le backend
  // (PILOTAGE_DASHBOARD_ENTRY_FIELDS, clients.py:116-121) → `.nullable()` SANS `.optional()`
  // (Codex R2 #1). Invariant applicatif P1-008 : non NULL ssi kind=waiting & status=en_attente.
  ball_in_court: pilotageBallInCourtSchema.nullable(),
  ball_in_court_since: z.string().nullable(),
  // Sources par champ (REFONDATION-PILOTAGE-LOT2-STORY-2.1 M1) : `{label: {kind, date}, ...}`.
  // Backend garantit un dict (jamais absent) : `.optional()` tolère les anciennes réponses/cache
  // avant déploiement, `.default({})` normalise à un dict vide. z.record enregistre les clés
  // dynamiques (field_name) sans contrainte d'énumération — le backend liste les champs qui
  // portent effectivement une source, inutile de les figer côté front.
  sources: z.record(z.string(), pilotageSourceSchema).optional().default({}),
  // Migration additive du 30-10 (§5) : facultatives, absentes avec l'API ancienne.
  is_blocking: z.boolean().optional().catch(undefined),
  due_date: z.string().nullable().optional().catch(undefined),
  contact_label: z.string().nullable().optional().catch(undefined),
  rdv_time: z.string().nullable().optional().catch(undefined),
});
export type PilotageEntry = z.infer<typeof pilotageEntrySchema>;

// ─── 4 objets métier V2 (run-005 story 2) — miroirs champ-par-champ des sérialiseurs backend
//     (clients.py:3666-3678 / :4657-4674). Enums = CHECK migration 189. Pas de `.strict()`. ───

export const pilotageJournalSchema = z.object({
  id: z.string(),
  event_type: pilotageJournalEventTypeSchema,
  title: z.string(),
  detail: z.string().nullable(),
  occurred_at: z.string().nullable(),
  is_future: z.boolean(),
  visibility: pilotageJournalVisibilitySchema,
  subject_type: pilotageJournalSubjectTypeSchema.nullable(),
  subject_id: z.string().nullable(),
  source: z.string(),
  feature_entry_ids: z.array(z.string()),
});
export type PilotageJournalEvent = z.infer<typeof pilotageJournalSchema>;

// `impact_days` retiré le 2026-08-18 (R005-FUITE-CLIENT-001) : jour vendu, jamais sur une surface
// client. Il reste stocké et reste servi aux écritures admin authentifiées.
export const pilotageRequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  status: pilotageRequestStatusSchema,
  decided_at: z.string().nullable(),
  decision_note: z.string().nullable(),
  feature_entry_ids: z.array(z.string()),
});
export type PilotageRequest = z.infer<typeof pilotageRequestSchema>;

export const pilotageFoundationSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: pilotageFoundationStatusSchema,
  detail: z.string().nullable(),
  sort_order: z.number().int(),
});
export type PilotageFoundation = z.infer<typeof pilotageFoundationSchema>;

export const pilotageReportSchema = z.object({
  id: z.string(),
  report_type: pilotageReportTypeSchema,
  body_markdown: z.string(),
  week_start: z.string().nullable(),
  run_date: z.string().nullable(),
  journal_event_id: z.string().nullable(),
});
export type PilotageReport = z.infer<typeof pilotageReportSchema>;

// Règles et questions (REFONDATION-PILOTAGE-LOT3-STORY-3.1, migration 249) : miroir de
// PILOTAGE_RULE_FIELDS + `source` (clients.py). Enums = CHECK de la migration 249.
export const pilotageRuleTypeSchema = z.enum(["regle", "question", "architecture"]);
export type PilotageRuleType = z.infer<typeof pilotageRuleTypeSchema>;
export const pilotageRuleStatusSchema = z.enum(["a_valider", "reponse_due", "validee", "close", "ecartee"]);
export type PilotageRuleStatus = z.infer<typeof pilotageRuleStatusSchema>;
export const pilotageRuleDueLevelSchema = z.enum(["convenue", "prevision", "a_convenir"]);

export const pilotageRuleSchema = z.object({
  id: z.string(),
  rule_type: pilotageRuleTypeSchema,
  statement: z.string(),
  detail: z.string().nullable(),
  status: pilotageRuleStatusSchema,
  decided_by_name: z.string().nullable(),
  decided_at: z.string().nullable(),
  due_date: z.string().nullable(),
  due_level: pilotageRuleDueLevelSchema.nullable(),
  replaced_by_id: z.string().nullable(),
  feature_entry_id: z.string().nullable(),
  sort_order: z.number().int(),
  source: pilotageSourceSchema.nullable(),
});
export type PilotageRule = z.infer<typeof pilotageRuleSchema>;

export const pilotageDashboardResponseSchema = z.object({
  item: pilotageDashboardItemSchema,
  // REFONDATION-PILOTAGE-LOT1.1 : nom du client pour le fil d'Ariane. `.optional()` : ce front part
  // avant ou avec le serveur ; une réponse sans la clé se rend comme une valeur nulle (pas de fil).
  client_name: z.string().nullable().optional(),
  subitems: z.array(pilotageDashboardSubitemSchema),
  dimensions: z.array(pilotageDimensionSchema),
  entries: z.array(pilotageEntrySchema),
  // 4 collections métier V2 — sérialisées par le dashboard étendu (clients.py:3605-3608).
  // Arrays REQUIS (jamais `.optional()`) : elles arrivent avec l'extension story 2 → verrouille
  // l'ordre de déploiement (backend story 2 déployé AVANT ce front — cf. story-3-decisions.md).
  journal: z.array(pilotageJournalSchema),
  requests: z.array(pilotageRequestSchema),
  foundations: z.array(pilotageFoundationSchema),
  reports: z.array(pilotageReportSchema),
  // Story 3.1 : `.optional().default([])` laisse ce front partir avant ou avec le serveur ; une
  // réponse sans la clé se rend comme un projet sans règle (onglet absent).
  rules: z.array(pilotageRuleSchema).optional().default([]),
  rdv_metier_count: z.number().int(),
  rdv_tech_count: z.number().int(),
  en_attente_client: z.boolean(),
  en_attente_count: z.number().int(),
  // REFONDATION-PILOTAGE-LOT2-STORY-2.2 — nombre d'attentes suspendues par la pause
  // (§3.7). Les statuts base restent inchangés, c'est une projection applicative.
  // `.optional().default(0)` pour tolérer les anciennes réponses avant déploiement.
  en_attente_suspended_count: z.number().int().optional().default(0),
  // Contrat §6 : rendez-vous enregistrés, résumé validé seulement. Absent avec l'API ancienne.
  rdvs: z.array(pilotageRdvSchema).optional().catch(undefined),
});
export type PilotageDashboardResponse = z.infer<typeof pilotageDashboardResponseSchema>;

// ─── Payloads de saisie manuelle Ailix (run-003 Story 12, spec §2.3/§2.5) ───────────
// Types OUTBOUND (validation au backend : 400 applicatif / 422 Pydantic). Les champs
// IMMUABLES en PATCH (spec §2.3 → 400 explicite) sont volontairement ABSENTS des types
// `*UpdatePayload` : les envoyer devient impossible par typage (GF-S12.2 / R003-ADV-S12.2).
// Immuables entry = entity_type/entity_id/kind SEULEMENT — `dimension` reste MUTABLE sur une
// entry (re-validé backend contre le kind persisté), donc il figure dans
// PilotageEntryUpdatePayload.

export interface PilotageEntryCreatePayload {
  entity_type: PilotageEntry["entity_type"];
  entity_id: string;
  kind: PilotageEntryKind;
  dimension?: PilotageDimensionKey | null;
  label: string;
  detail?: string | null;
  status?: string | null;
  entry_date?: string | null;
  sort_order?: number;
  // P1-008 branche 1 : une attente créée `en_attente` doit porter ses 2 colonnes ball-in-court dès
  // le POST, sinon le backend répond 400 (`create_pilotage_entry` applique la même table de vérité
  // que le PATCH). Sans ces clés, aucune « pièce en attente » n'était créable depuis le panneau admin.
  ball_in_court?: string | null;
  ball_in_court_since?: string | null;
}

export interface PilotageEntryUpdatePayload {
  dimension?: PilotageDimensionKey | null;
  label?: string;
  detail?: string | null;
  status?: string | null;
  entry_date?: string | null;
  sort_order?: number;
  // P1-008 : une entry `recu` doit avoir ses 2 colonnes ball-in-court à NULL. Le backend valide
  // l'état FINAL du merge (clients.py) → un PATCH qui bascule sans les vider répond 400.
  ball_in_court?: string | null;
  ball_in_court_since?: string | null;
  // Migration additive du 30-10 (§6) : listes blanches des routes de modification d'une entrée.
  is_blocking?: boolean;
  due_date?: string | null;
  contact_label?: string | null;
  rdv_time?: string | null;
}

// ─── Saisies salariés de la nouvelle fiche (lot 6, contrat §6) ───
// Route actuelle de modification d'un item : champs de clôture et contenu de la version en
// production. `prod_version_label` et `prod_version_date` gardent leur panneau actuel (§4.6).
export type PilotageClosureState = "terminee" | "abandonnee";

export interface PilotageItemFichePayload {
  closure_state?: PilotageClosureState | null;
  closure_date?: string | null;
  prod_version_content?: string | null;
}

// POST et PATCH .../versions[/{version_id}] : versions à venir. `archived: true` archive la
// ligne, comme pour un item de pilotage.
export interface PilotageVersionCreatePayload {
  label: string;
  state: PilotageVersionState;
  content?: string | null;
  target_date?: string | null;
}

export type PilotageVersionUpdatePayload = Partial<PilotageVersionCreatePayload> & { archived?: boolean };

// GET et PUT .../entries/{entry_id}/rdv-summary, POST .../rdv-summary/validate (§4.4) : le
// brouillon n'est renvoyé que par ces routes salariés.
export const pilotageRdvSummaryDraftSchema = z.object({
  entry_id: z.string(),
  summary: z.string().nullable(),
  validated_at: z.string().nullable().optional(),
  validated_by: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type PilotageRdvSummaryDraft = z.infer<typeof pilotageRdvSummaryDraftSchema>;

// Story 3.1 : écritures Ailix des règles (POST / PATCH .../rules). Clé absente = préserver ;
// null = vider un champ optionnel (refusé sur les colonnes obligatoires, 422).
export interface PilotageRuleCreatePayload {
  rule_type: PilotageRuleType;
  statement: string;
  status?: PilotageRuleStatus;
  detail?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  due_date?: string | null;
}

export type PilotageRuleUpdatePayload = Partial<PilotageRuleCreatePayload>;
